import { useReactiveVar } from '@apollo/client';
import { SsoQueryField } from '@common/neo.tax';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { apolloClient } from '@neo.tax/shared/apollo';
import { LocalStorageKey } from '@neo.tax/shared/enums';
import { reactiveVarOrchestrator } from '@neo.tax/shared/orchestrators';
import { onAuthenticationStateChange, SsoProvider, migrateSsoUser } from '@neo.tax/shared/third-parties';
import { clearThirdParties, startTrackingUserInactivity, stopTrackingUserInactivity } from '@neo.tax/shared/utils';

import * as AuthenticationState from '../authenticationStateVar';

// Clear session keys from localStorage
const clearLocalStorage = (): void => {
  Object.entries(LocalStorageKey)
    .filter(([key]) => !key.startsWith('PERSIST_'))
    .forEach(([_, value]) => window.localStorage.removeItem(value));
};

export const useAuthenticationService = (): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    authenticationServiceState: { authenticationUser },
  } = useReactiveVar(AuthenticationState.authenticationStateVar);

  useEffect(() => {
    onAuthenticationStateChange((user) => {
      AuthenticationState.updateAuthenticationUser(user);

      // When user signs in
      if (!authenticationUser && user) {
        startTrackingUserInactivity();

        // handle migration from old SSO (custom token auth) to new SSO (Firebase SSO auth)
        const ssoProvider = searchParams.get(SsoQueryField.SSO_PROVIDER);
        if (ssoProvider) {
          setSearchParams((searchParams) => {
            searchParams.delete(SsoQueryField.SSO_PROVIDER);
            return searchParams;
          });
          migrateSsoUser(user, ssoProvider as SsoProvider);
        }
        return;
      }

      // When user signs out
      if (authenticationUser && !user) {
        // IF user is null THEN proceed with logout
        stopTrackingUserInactivity();
        // TODO: resolve circular reference in reactive vars
        //https://linear.app/neo-tax/issue/NEO-3043/resolve-circular-reference-in-reactive-vars
        reactiveVarOrchestrator.clearReactiveVars();
        AuthenticationState.resetAuthentication();
        clearLocalStorage();
        window.sessionStorage.clear();
        apolloClient.clearStore();
        // window.HubSpotConversations?.widget.refresh();
        clearThirdParties();
      }
    });
  }, [authenticationUser, searchParams, setSearchParams]);
};
